/**
 * DomainAnalysisView component for domain-level analytics.
 */

import { useState } from 'react'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { ChevronLeft, ChevronRight, ArrowUpDown } from 'lucide-react'
import { useDomainStats } from '../hooks/use-stats'
import type { Scope } from '../types'

interface DomainAnalysisViewProps {
  scope: Scope
  tenantId: string | null
  organizationId: string | null
  start: string
  end: string
}

export function DomainAnalysisView({
  scope,
  tenantId,
  organizationId,
  start,
  end
}: DomainAnalysisViewProps) {
  const [page, setPage] = useState(1)
  const [limit, setLimit] = useState(20)
  const [sortBy, setSortBy] = useState('users')

  const { data, isLoading, error } = useDomainStats({
    page,
    limit,
    sort_by: sortBy,
    scope,
    tenantId,
    organizationId,
    start,
    end
  })

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="text-center text-destructive">
            Error loading domain stats: {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        </CardContent>
      </Card>
    )
  }

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="h-64 bg-muted animate-pulse rounded" />
        </CardContent>
      </Card>
    )
  }

  const domains = data?.items || []
  const pageInfo = data?.page
  const totalPages = pageInfo ? Math.ceil(pageInfo.total / pageInfo.limit) : 1

  // Calculate summary from top domains
  const topDomains = domains.slice(0, 5)
  const summaryStats = topDomains.reduce(
    (acc, domain) => ({
      totalUsers: acc.totalUsers + domain.users,
      totalAgents: acc.totalAgents + domain.agents,
      totalTokens: acc.totalTokens + domain.tokens,
      totalCost: acc.totalCost + domain.cost
    }),
    { totalUsers: 0, totalAgents: 0, totalTokens: 0, totalCost: 0 }
  )

  const handleSort = (column: string) => {
    setSortBy(column)
    setPage(1) // Reset to first page when sorting changes
  }

  return (
    <div className="space-y-6">
      {/* Summary Cards */}
      {topDomains.length > 0 && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top 5 Domains - Users</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryStats.totalUsers.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top 5 Domains - Agents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryStats.totalAgents.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top 5 Domains - Tokens</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{summaryStats.totalTokens.toLocaleString()}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Top 5 Domains - Cost</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">${summaryStats.totalCost.toFixed(2)}</div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Domain Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle>Domain Statistics</CardTitle>
              <CardDescription>
                Breakdown of metrics by email domain
              </CardDescription>
            </div>
            <div className="flex items-center gap-2">
              <Select value={limit.toString()} onValueChange={(val) => setLimit(parseInt(val))}>
                <SelectTrigger className="w-32">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 per page</SelectItem>
                  <SelectItem value="20">20 per page</SelectItem>
                  <SelectItem value="50">50 per page</SelectItem>
                  <SelectItem value="100">100 per page</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="text-left p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('domain')}
                      className="flex items-center gap-1"
                    >
                      Domain
                      <ArrowUpDown className="h-3 w-3" />
                    </Button>
                  </th>
                  <th className="text-right p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('users')}
                      className="flex items-center gap-1 ml-auto"
                    >
                      Users
                      <ArrowUpDown className="h-3 w-3" />
                    </Button>
                  </th>
                  <th className="text-right p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('agents')}
                      className="flex items-center gap-1 ml-auto"
                    >
                      Agents
                      <ArrowUpDown className="h-3 w-3" />
                    </Button>
                  </th>
                  <th className="text-right p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('tokens')}
                      className="flex items-center gap-1 ml-auto"
                    >
                      Tokens
                      <ArrowUpDown className="h-3 w-3" />
                    </Button>
                  </th>
                  <th className="text-right p-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleSort('cost')}
                      className="flex items-center gap-1 ml-auto"
                    >
                      Cost
                      <ArrowUpDown className="h-3 w-3" />
                    </Button>
                  </th>
                </tr>
              </thead>
              <tbody>
                {domains.map((domain, idx) => (
                  <tr key={domain.domain} className="border-b hover:bg-muted/50">
                    <td className="p-2 font-medium">
                      <div className="flex items-center gap-2">
                        {idx < 3 && (
                          <span className="text-xs font-bold text-muted-foreground">
                            #{idx + 1}
                          </span>
                        )}
                        {domain.domain}
                      </div>
                    </td>
                    <td className="text-right p-2">{domain.users.toLocaleString()}</td>
                    <td className="text-right p-2">{domain.agents.toLocaleString()}</td>
                    <td className="text-right p-2">{domain.tokens.toLocaleString()}</td>
                    <td className="text-right p-2">${domain.cost.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
            {domains.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No domain data available for the selected scope
              </div>
            )}
          </div>

          {/* Pagination */}
          {pageInfo && pageInfo.total > 0 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t">
              <div className="text-sm text-muted-foreground">
                Showing {pageInfo.offset + 1} to {Math.min(pageInfo.offset + pageInfo.limit, pageInfo.total)} of {pageInfo.total} domains
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  <ChevronLeft className="h-4 w-4 mr-1" />
                  Previous
                </Button>
                <div className="text-sm">
                  Page {page} of {totalPages}
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                >
                  Next
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
